// Shift formatting and leave-related utilities

import moment from 'moment-timezone';
import { RosterShift } from './types';
import type { TimeFormatPreference } from '@/utils/timeFormat';

/**
 * Helper: Check if task should be hidden (shiftTasks format)
 */
function isHiddenShiftTask(shift: RosterShift): boolean {
    if ((shift.shiftTasks || []).length !== 1) {
        return false;
    }
    
    const task = shift.shiftTasks![0];
    return !!(task.taskType?.isLeave && task.taskType?.hideLeaveInAgentViews);
}

/**
 * Helper: Check if task should be hidden (legacy tasks format)
 */
function isHiddenTask(shift: RosterShift): boolean {
    if ((shift.tasks || []).length !== 1) {
        return false;
    }
    
    const task = shift.tasks![0];
    return !!(task.isLeave && (task.hideLeaveInAgentViews || task.taskType?.hideLeaveInAgentViews));
}

/**
 * Helper: Check if shift is a hidden leave shift
 */
function isHiddenLeaveShift(shift: RosterShift): boolean {
    // NEW: Check shiftTasks first (new API format)
    if (isHiddenShiftTask(shift)) {
        return true;
    }
    
    // OLD: Fallback to tasks (legacy format)
    return isHiddenTask(shift);
}

/**
 * Helper: Get leave text from shiftTasks (new API format)
 */
function getLeaveTextFromShiftTasks(shift: RosterShift): string | null {
    if (!shift.shiftTasks) {
        return null;
    }
    
    const shiftLeaveTask = shift.shiftTasks.find((task) => task.taskType?.isLeave === true);
    if (shiftLeaveTask?.taskType) {
        return shiftLeaveTask.taskType.name || 'Leave';
    }
    
    return null;
}

/**
 * Helper: Get leave text from tasks (legacy format)
 */
function getLeaveTextFromTasks(
    shift: RosterShift,
    leaveTasks: Array<{ id: string; name: string }>
): string | null {
    if (!shift.tasks) {
        return null;
    }
    
    const shiftLeaveTask = shift.tasks.find((task) => task.isLeave === true);
    if (!shiftLeaveTask) {
        return null;
    }
    
    const matchedLeaveTask = leaveTasks.find((item) => item.id === shiftLeaveTask.id);
    return matchedLeaveTask ? matchedLeaveTask.name : 'Leave';
}

/**
 * Helper: Format leave shift text
 */
function formatLeaveShift(
    shift: RosterShift,
    leaveTasks: Array<{ id: string; name: string }>,
    hasViewLeaveTypePermission: boolean
): string {
    if (!hasViewLeaveTypePermission) {
        return 'Leave';
    }
    
    // Try new API format first
    const leaveTextFromShiftTasks = getLeaveTextFromShiftTasks(shift);
    if (leaveTextFromShiftTasks) {
        return leaveTextFromShiftTasks;
    }
    
    // Fallback to legacy format
    const leaveTextFromTasks = getLeaveTextFromTasks(shift, leaveTasks);
    if (leaveTextFromTasks) {
        return leaveTextFromTasks;
    }
    
    return 'Leave';
}

/**
 * Helper: Format regular shift time range
 * Supports both 12h (with AM/PM) and 24h formats based on user preference
 *
 * @param shift - Roster shift object
 * @param timezone - User timezone for formatting times
 * @param timeFormatPreference - User's time format preference (12h or 24h)
 * @returns Formatted time range string (e.g., "9:00 AM - 5:00 PM" or "09:00 - 17:00")
 */
function formatTimeRange(
    shift: RosterShift,
    timezone: string,
    timeFormatPreference?: TimeFormatPreference | null
): string {
    const startMoment = moment.tz(shift.startTime, timezone);
    const endMoment = moment.tz(shift.endTime, timezone);
    
    // Determine format based on preference (default to 24h)
    // Note: 'hh:mm A' pads single-digit hours with leading zero for better alignment
    const is12HourFormat = timeFormatPreference?.timeFormat === '12h';
    const timeFormat = is12HourFormat ? 'hh:mm A' : 'HH:mm';
    
    const formattedStartTime = startMoment.format(timeFormat);
    const formattedEndTime = endMoment.format(timeFormat);
    
    return `${formattedStartTime} - ${formattedEndTime}`;
}

/**
 * Format shift duration as "HH:mm - HH:mm" or "Leave" text
 * Modified from Angular My Team Roster formatShiftDuration() function (lines 115-140)
 *
 * VUE MODIFICATION: Unlike Angular which hides RDO shifts (returns ''),
 * we show the actual leave type name (e.g., "RDO") for better transparency
 *
 * Logic:
 * 1. If leave shift with hideLeaveInAgentViews → return leave task name (e.g., "RDO")
 * 2. If leave shift → return leave task name or 'Leave'
 * 3. Otherwise → return 'HH:mm - HH:mm' (or 'h:mm A - h:mm A' for 12h format)
 *
 * @param shift - Roster shift object with tasks array
 * @param timezone - User timezone for formatting times
 * @param options - Formatting options
 */
export function formatShiftDuration(
    shift: RosterShift,
    timezone: string,
    options: {
        leaveTasks?: Array<{ id: string; name: string }>;
        hasViewLeaveTypePermission?: boolean;
        timeFormatPreference?: TimeFormatPreference | null;
    } = {}
): string {
    const {
        leaveTasks = [],
        hasViewLeaveTypePermission = false,
        timeFormatPreference
    } = options;
    if (!shift) {
        return '';
    }

    // Step 1: Check if this is a partial leave (shift + leave)
    // For partial leaves, show the shift time (not the leave)
    if (isPartialLeave(shift)) {
        return formatTimeRange(shift, timezone, timeFormatPreference);
    }

    // Step 2: Check if this is a hidden leave shift (RDO, etc.)
    // VUE CHANGE: Show the leave name instead of hiding it
    if (isHiddenLeaveShift(shift)) {
        // Get the leave task name
        const leaveName = formatLeaveShift(shift, leaveTasks, true); // Always show name for hidden leaves
        return leaveName || 'RDO'; // Fallback to 'RDO' if name not found
    }

    // Step 3: Check if this is a full-day leave shift
    const isLeave = isLeaveShift(shift);
    if (isLeave) {
        return formatLeaveShift(shift, leaveTasks, hasViewLeaveTypePermission);
    }

    // Step 4: Regular shift - format as "HH:mm - HH:mm" or "h:mm A - h:mm A"
    return formatTimeRange(shift, timezone, timeFormatPreference);
}

/**
 * @param shift - The roster shift to check
 * @returns true if the shift has both leave and non-leave tasks, false otherwise
 */
export function isPartialLeave(shift: RosterShift): boolean {
    if (!shift) {
        return false;
    }
    
    if (shift.shiftTasks && shift.shiftTasks.length > 1) {
        const hasLeaveTask = shift.shiftTasks.some((task) => task.taskType?.isLeave === true);
        const hasNonLeaveTask = shift.shiftTasks.some((task) => task.taskType?.isLeave === false);
        return hasLeaveTask && hasNonLeaveTask;
    }
    return false;
}

/**
 * Get leave duration text for a partial leave to display in tooltip
 *
 * @param shift
 * @param options
 * @param options.timezone
 * @param options.leaveTasks
 * @param options.hasViewLeaveTypePermission
 * @param options.timeFormatPreference
 * @returns Formatted leave duration string (e.g., "Annual Leave: 1:00 PM - 5:00 PM") or undefined
 * getPartialLeaveDuration(shift, { timezone: 'America/New_York', leaveTasks: [], hasViewLeaveTypePermission: false, timeFormatPreference: { timeFormat: '12h' } })
 */
function formatLeaveTaskDuration(
    leaveTask: { startTime: string; endTime: string; taskType?: { name?: string; isLeave?: boolean } },
    timezone: string,
    timeFormat: string,
    hasViewLeaveTypePermission: boolean
): string {
    const startTime = moment.tz(leaveTask.startTime, timezone).format(timeFormat);
    const endTime = moment.tz(leaveTask.endTime, timezone).format(timeFormat);
    const leaveName = hasViewLeaveTypePermission && leaveTask.taskType?.name
        ? leaveTask.taskType.name
        : 'Leave';
    return `${leaveName}: ${startTime} - ${endTime}`;
}

function extractLeaveTasksFromShift(
    shift: RosterShift
): Array<{ startTime: string; endTime: string; taskType?: { name?: string; isLeave?: boolean } }> {
    if (!shift.shiftTasks) {
        return [];
    }
    const leaveTasks = shift.shiftTasks
        .filter((task) => task.taskType?.isLeave === true && task.startTime && task.endTime)
        .map((task) => ({
            startTime: task.startTime!,
            endTime: task.endTime!,
            taskType: task.taskType
        }));
    return leaveTasks;
}

export function getPartialLeaveDuration(
    shift: RosterShift,
    options: {
        timezone: string;
        leaveTasks: Array<{ id: string; name: string }>;
        hasViewLeaveTypePermission: boolean;
        timeFormatPreference?: TimeFormatPreference | null;
    }
): string | undefined {
    if (!shift || !options.timezone || !isPartialLeave(shift)) {
        return undefined;
    }
    
    const leaveTasks = extractLeaveTasksFromShift(shift);
    if (leaveTasks.length === 0) {
        return undefined;
    }
    
    const is12HourFormat = options.timeFormatPreference?.timeFormat === '12h';
    const timeFormat = is12HourFormat ? 'h:mm A' : 'HH:mm';
    
    const formattedLeaveDurations = leaveTasks.map((leaveTask) =>
        formatLeaveTaskDuration(
            leaveTask,
            options.timezone,
            timeFormat,
            options.hasViewLeaveTypePermission
        )
    );
    
    return formattedLeaveDurations.join('\n');
}

/**
 * Check if shift is a leave shift
 * Updated to support both old 'tasks' format and new 'shiftTasks' format
 */
export function isLeaveShift(shift: RosterShift): boolean {
    // NEW: Check shiftTasks first (new API format)
    if (shift.shiftTasks) {
        const leaveTask = shift.shiftTasks.find((task) => task.taskType?.isLeave === true);
        return !!leaveTask;
    }
    // OLD: Fallback to tasks (legacy format)
    if (shift.tasks) {
        const leaveTask = shift.tasks.find((task) => task.isLeave === true);
        return !!leaveTask;
    }
    return false;
}

export function pickTextColorBasedOnBgColorSimple(
    bgColor: string,
    lightColor = 'white',
    darkColor = 'black'
): string {
    if (!bgColor) return darkColor;
    
    // Remove # if present
    const color = bgColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(color.substr(0, 2), 16);
    const g = parseInt(color.substr(2, 2), 16);
    const b = parseInt(color.substr(4, 2), 16);
    
    // Calculate brightness (YIQ formula)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    
    // Return light color for dark backgrounds, dark color for light backgrounds
    return brightness < 128 ? lightColor : darkColor;
}
